import type { MarketingInvoiceItemInput } from '../../types/marketingNotas'
import type { MarketingRequest, MarketingRequestItem } from '../../types/marketingSolicitacao'
import type {
  SupplierQuote,
  SupplierQuoteItemInput,
  SupplierQuoteStatus,
} from '../../types/supplierQuotes'
import { supplierQuoteItemsTotal } from '../../services/supplierQuotes'
import {
  isStockDeductibleItemType,
  parseItemQuantity,
  formatRequestNumber,
} from './solicitacaoUi'
import { formatCurrency, formatDateOnlyPtBr } from './notasUi'

export const MODULE_NAME = 'marketing_propostas'
export const PERM_LOADING = null
export const PAGE_SIZE = 20

export const SELECT_CLASS =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white'

export const TEXTAREA_CLASS =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white'

export const ICON_BTN_CLASS =
  'rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-gray-700'

export const ICON_BTN_DANGER_CLASS =
  'rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20'

export const STATUS_LABELS: Record<SupplierQuoteStatus, string> = {
  rascunho: 'Rascunho',
  recebida: 'Recebida',
  nota_gerada: 'Nota gerada',
  descartada: 'Descartada',
}

export function statusLabel(status: SupplierQuoteStatus | string): string {
  return STATUS_LABELS[status as SupplierQuoteStatus] || status
}

export function statusBadgeClass(status: SupplierQuoteStatus | string): string {
  if (status === 'recebida') {
    return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
  }
  if (status === 'nota_gerada') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
  }
  if (status === 'descartada') {
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
  }
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
}

export function formatQuoteNumber(n: number | null | undefined): string {
  if (n == null) return '—'
  return `#${String(n).padStart(4, '0')}`
}

export function formatQuoteSelectLabel(quote: SupplierQuote): string {
  const number = formatQuoteNumber(quote.quote_number)
  const supplier = quote.supplier?.name || 'Sem fornecedor'
  const request = quote.request?.request_number
    ? ` · Sol. ${formatRequestNumber(quote.request.request_number)}`
    : ''
  return `${number} · ${supplier}${request} · ${quote.title}`
}

export function quoteTotal(quote: SupplierQuote): number {
  return supplierQuoteItemsTotal(quote)
}

export { formatCurrency, formatDateOnlyPtBr }

export function emptyQuoteItem(): SupplierQuoteItemInput {
  return {
    stock_item_id: '',
    request_item_id: null,
    description: '',
    quantity: 1,
    unit: '',
    unit_value: 0,
  }
}

function itemLabelFromRequest(item: MarketingRequestItem): string {
  const details = (item.details || {}) as Record<string, unknown>
  const fromStock = String(details.item || details.material || '').trim()
  if (fromStock) return fromStock
  const fromText = String(
    details.descricao ||
      details.formato ||
      details.o_que_marketing_fornece ||
      details.mensagem_principal ||
      details.canal ||
      ''
  ).trim()
  if (fromText) return fromText
  return item.item_type
}

export function itemsFromRequest(request: MarketingRequest | null | undefined): SupplierQuoteItemInput[] {
  const result: SupplierQuoteItemInput[] = []
  for (const item of request?.items || []) {
    if (item.deleted_at) continue
    const details = (item.details || {}) as Record<string, unknown>
    const stockItemId = String(details.item_id || '').trim()
    const qty = parseItemQuantity(details.quantidade)
    const quantity = Number.isFinite(qty) && qty > 0 ? qty : 1
    const label = itemLabelFromRequest(item)

    if (isStockDeductibleItemType(item.item_type) && stockItemId) {
      result.push({
        stock_item_id: stockItemId,
        request_item_id: item.id,
        description: label,
        quantity,
        unit: '',
        unit_value: 0,
      })
      continue
    }

    result.push({
      stock_item_id: null,
      request_item_id: item.id,
      description: label,
      quantity,
      unit: '',
      unit_value: 0,
    })
  }
  return result.length > 0 ? result : [emptyQuoteItem()]
}

export function invoiceItemsFromQuote(quote: SupplierQuote): MarketingInvoiceItemInput[] {
  const rows = (quote.items || []).map((item, index) => {
    const quantity = Number(item.quantity || 0)
    const unitValue = Number(item.unit_value || 0)
    const total = Math.round(quantity * unitValue * 10000) / 10000
    return {
      stock_item_id: item.stock_item_id,
      description: item.description,
      quantity,
      unit_value: unitValue,
      discount_value: 0,
      total_value: total,
      sort_order: item.sort_order ?? index * 10,
    }
  })
  return rows.length > 0
    ? rows
    : [
        {
          stock_item_id: '',
          description: '',
          quantity: 1,
          unit_value: 0,
          discount_value: 0,
          total_value: 0,
        },
      ]
}

export type CompareRow = {
  key: string
  label: string
  unit: string | null
  cells: Record<string, { quantity: number; unitValue: number; lineTotal: number } | null>
}

export function buildCompareMatrix(quotes: SupplierQuote[]): {
  rows: CompareRow[]
  totals: Record<string, number>
  bestQuoteId: string | null
} {
  const rowMap = new Map<string, CompareRow>()

  for (const quote of quotes) {
    for (const item of quote.items || []) {
      const stockKey = item.stock_item_id || ''
      const descKey = (item.description || '').trim().toLowerCase()
      const key = stockKey ? `stock:${stockKey}` : `desc:${descKey || item.id}`
      const label =
        item.stock_item?.name ||
        item.description ||
        (item.stock_item_id ? 'Item de estoque' : 'Item')
      let row = rowMap.get(key)
      if (!row) {
        row = {
          key,
          label,
          unit: item.unit || item.stock_item?.unit || null,
          cells: {},
        }
        rowMap.set(key, row)
      }
      const quantity = Number(item.quantity || 0)
      const unitValue = Number(item.unit_value || 0)
      row.cells[quote.id] = {
        quantity,
        unitValue,
        lineTotal: Math.round(quantity * unitValue * 10000) / 10000,
      }
    }
  }

  const totals: Record<string, number> = {}
  let bestQuoteId: string | null = null
  let bestTotal = Number.POSITIVE_INFINITY
  for (const quote of quotes) {
    const total = quoteTotal(quote)
    totals[quote.id] = total
    if (total < bestTotal) {
      bestTotal = total
      bestQuoteId = quote.id
    }
  }

  return {
    rows: Array.from(rowMap.values()),
    totals,
    bestQuoteId,
  }
}

export function lowestUnitValueForRow(
  row: CompareRow,
  quoteIds: string[]
): number | null {
  let min: number | null = null
  for (const id of quoteIds) {
    const cell = row.cells[id]
    if (!cell) continue
    if (min == null || cell.unitValue < min) min = cell.unitValue
  }
  return min
}
